import type {
  IngestionPipeline,
  LakehouseApi,
  Workspace,
} from '@repo/lakehouse-sdk';
import { WorkspaceStatus } from '@repo/lakehouse-sdk';

export interface ProjectIngestionPipeline {
  workspace: Workspace;
  pipeline: IngestionPipeline;
}

const PAGE_SIZE = 200;

const collectPages = async <TResponse, TItem>(
  fetchPage: (pageToken: string | undefined) => Promise<TResponse>,
  itemsOf: (response: TResponse) => TItem[],
  nextTokenOf: (response: TResponse) => string | null | undefined
): Promise<TItem[]> => {
  const items: TItem[] = [];
  let pageToken: string | undefined;
  do {
    const response = await fetchPage(pageToken);
    items.push(...itemsOf(response));
    pageToken = nextTokenOf(response) ?? undefined;
  } while (pageToken);
  return items;
};

/**
 * Lakehouse lists pipelines per workspace, while an approval rule covers the
 * whole project, so a rule's picker has to read every workspace. A failure in
 * any workspace fails the whole list: a picker missing one workspace's streams
 * would let a rule be saved without the streams the user meant to protect. A
 * deleted workspace holds no streams, so it is not asked.
 */
export const listProjectIngestionPipelines = async (
  api: Pick<LakehouseApi, 'listWorkspaces' | 'listIngestionPipelines'>,
  projectId: string
): Promise<ProjectIngestionPipeline[]> => {
  const workspaces = await collectPages(
    (pageToken) =>
      api.listWorkspaces(projectId, pageToken, PAGE_SIZE, {
        filters: {
          status: { notIn: [WorkspaceStatus.WORKSPACE_STATUS_DELETED] },
        },
      }),
    (response) => response.workspaces,
    (response) => response.nextToken
  );
  const perWorkspace = await Promise.all(
    workspaces.map(async (workspace) => {
      const pipelines = await collectPages(
        (pageToken) =>
          api.listIngestionPipelines(
            projectId,
            workspace.id,
            pageToken,
            PAGE_SIZE
          ),
        (response) => response.ingestionPipelines,
        (response) => response.nextToken
      );
      return pipelines.map((pipeline) => ({ workspace, pipeline }));
    })
  );
  return perWorkspace.flat();
};
